import React, { useState } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from '../../../components/AppIcon';

const MEALS = ['Breakfast', 'Lunch', 'Dinner', 'Snack 1', 'Snack 2'];

const SortableFoodItem = ({ item, onRemove, onUpdateQty }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item?.id });
  const style = { transform: CSS?.Transform?.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const totalCals = Math.round((item?.calories || 0) * (item?.quantity || 1));
  const totalProtein = Math.round((item?.protein || 0) * (item?.quantity || 1) * 10) / 10;
  const totalCarbs = Math.round((item?.carbs || 0) * (item?.quantity || 1) * 10) / 10;
  const totalFats = Math.round((item?.fats || 0) * (item?.quantity || 1) * 10) / 10;

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center gap-2 p-2 bg-card border border-border rounded-lg group hover:border-primary/30 transition-all">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0">
          <Icon name="GripVertical" size={14} className="text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-foreground truncate">{item?.name}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Qty:</span>
              <input
                type="number"
                value={item?.quantity || 1}
                onChange={e => onUpdateQty(item?.id, parseFloat(e?.target?.value) || 0.5)}
                className="w-12 text-xs text-center bg-muted border border-border rounded px-1 py-0.5 text-foreground"
                min="0.5"
                step="0.5"
              />
              <span className="text-xs text-muted-foreground">× {item?.servingSize}</span>
            </div>
            <span className="text-xs text-red-400">{totalCals} kcal</span>
            <span className="text-xs text-blue-400">P:{totalProtein}g</span>
            <span className="text-xs text-yellow-400">C:{totalCarbs}g</span>
            <span className="text-xs text-orange-400">F:{totalFats}g</span>
          </div>
        </div>
        <button
          onClick={() => onRemove(item?.id)}
          className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all opacity-0 group-hover:opacity-100"
        >
          <Icon name="X" size={12} />
        </button>
      </div>
    </div>
  );
};

const MacroBar = ({ label, value, target, color }) => {
  const pct = target > 0 ? Math.min((value / target) * 100, 100) : 0;
  const over = target > 0 && value > target;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-6">{label}</span>
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${over ? 'bg-destructive' : color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-medium w-14 text-right ${over ? 'text-destructive' : 'text-foreground'}`}>
        {value}g{target > 0 ? `/${target}g` : ''}
      </span>
    </div>
  );
};

const MealColumn = ({ meal, mealIndex, items, onDrop, onRemoveItem, onUpdateQty, dayTargets }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active?.id !== over?.id) {
      const oldIndex = items?.findIndex(i => i?.id === active?.id);
      const newIndex = items?.findIndex(i => i?.id === over?.id);
      onDrop(mealIndex, arrayMove(items, oldIndex, newIndex));
    }
  };

  const totals = items?.reduce((acc, item) => ({
    calories: acc?.calories + Math.round((item?.calories || 0) * (item?.quantity || 1)),
    protein: acc?.protein + (item?.protein || 0) * (item?.quantity || 1),
    carbs: acc?.carbs + (item?.carbs || 0) * (item?.quantity || 1),
    fats: acc?.fats + (item?.fats || 0) * (item?.quantity || 1),
  }), { calories: 0, protein: 0, carbs: 0, fats: 0 });

  totals.protein = Math.round(totals?.protein * 10) / 10;
  totals.carbs = Math.round(totals?.carbs * 10) / 10;
  totals.fats = Math.round(totals?.fats * 10) / 10;

  const mealIcons = { Breakfast: 'Sunrise', Lunch: 'Sun', Dinner: 'Moon', 'Snack 1': 'Apple', 'Snack 2': 'Cookie' };

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card min-h-[220px]">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Icon name={mealIcons?.[meal] || 'UtensilsCrossed'} size={14} className="text-primary" />
          <p className="text-xs font-bold text-foreground">{meal}</p>
        </div>
        {items?.length > 0 && (
          <span className="text-xs text-muted-foreground">{totals?.calories} kcal</span>
        )}
      </div>
      <div className="flex-1 p-2 space-y-1.5 overflow-y-auto">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items?.map(i => i?.id)} strategy={verticalListSortingStrategy}>
            {items?.map(item => (
              <SortableFoodItem
                key={item?.id}
                item={item}
                onRemove={(id) => onRemoveItem(mealIndex, id)}
                onUpdateQty={(id, qty) => onUpdateQty(mealIndex, id, qty)}
              />
            ))}
          </SortableContext>
        </DndContext>
        {items?.length === 0 && (
          <div className="flex items-center justify-center py-6 border-2 border-dashed border-border/50 rounded-lg">
            <p className="text-xs text-muted-foreground text-center">Drag foods here</p>
          </div>
        )}
      </div>
      {items?.length > 0 && (
        <div className="px-3 py-2 border-t border-border/50 space-y-1">
          <div className="flex gap-3 text-xs mb-1">
            <span className="text-blue-400">P: {totals?.protein}g</span>
            <span className="text-yellow-400">C: {totals?.carbs}g</span>
            <span className="text-orange-400">F: {totals?.fats}g</span>
          </div>
        </div>
      )}
    </div>
  );
};

const DailyMealPlannerTimeline = ({ mealDays, onDropToMeal, onRemoveFromMeal, onUpdateQty, dayTargets }) => {
  const [selectedDay, setSelectedDay] = useState(0);
  const currentDay = mealDays?.[selectedDay];

  const dayTotals = currentDay?.meals?.reduce((acc, meal) => {
    meal?.foods?.forEach(item => {
      acc.calories += Math.round((item?.calories || 0) * (item?.quantity || 1));
      acc.protein += (item?.protein || 0) * (item?.quantity || 1);
      acc.carbs += (item?.carbs || 0) * (item?.quantity || 1);
      acc.fats += (item?.fats || 0) * (item?.quantity || 1);
    });
    return acc;
  }, { calories: 0, protein: 0, carbs: 0, fats: 0 }) || { calories: 0, protein: 0, carbs: 0, fats: 0 };

  dayTotals.protein = Math.round(dayTotals?.protein * 10) / 10;
  dayTotals.carbs = Math.round(dayTotals?.carbs * 10) / 10;
  dayTotals.fats = Math.round(dayTotals?.fats * 10) / 10;

  const calPct = dayTargets?.calories > 0 ? Math.min((dayTotals?.calories / dayTargets?.calories) * 100, 100) : 0;
  const calOver = dayTargets?.calories > 0 && dayTotals?.calories > dayTargets?.calories;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
          <Icon name="CalendarDays" size={16} className="text-secondary" />
        </div>
        <h2 className="text-base font-bold text-foreground">Daily Meal Planner</h2>
        <span className="ml-auto text-xs text-muted-foreground">
          {mealDays?.reduce((acc, d) => acc + d?.meals?.reduce((a, m) => a + m?.foods?.length, 0), 0)} total foods
        </span>
      </div>
      {/* Day selector */}
      <div className="flex gap-1 mb-3 overflow-x-auto pb-1">
        {mealDays?.map((day, idx) => {
          const hasFoods = day?.meals?.some(m => m?.foods?.length > 0);
          return (
            <button
              key={idx}
              onClick={() => setSelectedDay(idx)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                selectedDay === idx ? 'bg-primary text-white' : hasFoods ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}
            >
              {day?.name}
            </button>
          );
        })}
      </div>
      {/* Day macro summary */}
      {(dayTotals?.calories > 0 || dayTargets?.calories > 0) && (
        <div className="mb-3 p-3 bg-muted/50 rounded-xl border border-border/50 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-foreground">Day Totals</span>
            <span className={`text-xs font-bold ${calOver ? 'text-destructive' : 'text-foreground'}`}>
              {dayTotals?.calories}{dayTargets?.calories > 0 ? ` / ${dayTargets?.calories}` : ''} kcal
            </span>
          </div>
          {dayTargets?.calories > 0 && (
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${calOver ? 'bg-destructive' : 'bg-primary'}`}
                style={{ width: `${calPct}%` }}
              />
            </div>
          )}
          <div className="space-y-1">
            <MacroBar label="P" value={dayTotals?.protein} target={dayTargets?.protein || 0} color="bg-blue-400" />
            <MacroBar label="C" value={dayTotals?.carbs} target={dayTargets?.carbs || 0} color="bg-yellow-400" />
            <MacroBar label="F" value={dayTotals?.fats} target={dayTargets?.fats || 0} color="bg-orange-400" />
          </div>
        </div>
      )}
      {/* Meals */}
      <div className="flex-1 overflow-y-auto space-y-3">
        {currentDay?.meals?.map((meal, mealIdx) => (
          <MealColumn
            key={meal?.name}
            meal={meal?.name}
            mealIndex={mealIdx}
            items={meal?.foods}
            onDrop={(mealIndex, newOrder) => onDropToMeal(selectedDay, mealIndex, newOrder)}
            onRemoveItem={(mealIndex, id) => onRemoveFromMeal(selectedDay, mealIndex, id)}
            onUpdateQty={(mealIndex, id, qty) => onUpdateQty(selectedDay, mealIndex, id, qty)}
            dayTargets={dayTargets}
          />
        ))}
      </div>
    </div>
  );
};

export default DailyMealPlannerTimeline;
